import { FullPageSkeleton } from '@/components/ui/skeleton'
import { endPoint } from '@/constant/end-point'
import useApiMutation from '@/hooks/use-api-mutation'
import { useAuth } from '@clerk/clerk-react'
import { useEffect, useRef, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'

const VENDOR_INVITATION_TOKEN_KEY = 'vendorInvitationToken'

interface IClaimExistingPayload {
  token: string
}

interface IClaimExistingResponse {
  organisationId?: string
}

interface IDefaultOrganisationPayload {
  organisationId: string
}

const VendorWelcomePage = () => {
  const { isLoaded, isSignedIn } = useAuth()
  const [searchParams] = useSearchParams()
  const [claiming, setClaiming] = useState(true)
  const claimAttempted = useRef(false)

  const { createMutation: claimMutation } = useApiMutation<IClaimExistingPayload>({
    endpoint: endPoint.portal.claimExisting,
    customQueryKey: [endPoint.portal.claimExisting],
  })

  const { createMutation: setDefaultOrgMutation } = useApiMutation<IDefaultOrganisationPayload>({
    endpoint: endPoint.defaultOrganisation,
    customQueryKey: [endPoint.defaultOrganisation],
  })

  useEffect(() => {
    if (!isLoaded || !isSignedIn || claimAttempted.current) return
    claimAttempted.current = true

    const token = searchParams.get('invitationToken') ?? localStorage.getItem(VENDOR_INVITATION_TOKEN_KEY)
    const claim = async () => {
      if (token) {
        try {
          const res = await claimMutation.mutateAsync({ token })
          const organisationId = (res.data as unknown as IClaimExistingResponse | undefined)?.organisationId
          if (organisationId) {
            await setDefaultOrgMutation.mutateAsync({ organisationId })
          }
          toast.success('Invitation accepted')
        } catch (err) {
          toast.error(
            err instanceof Error
              ? err.message
              : 'Could not accept the invitation. It may have expired or already been used.'
          )
        } finally {
          localStorage.removeItem(VENDOR_INVITATION_TOKEN_KEY)
        }
      }
      setClaiming(false)
    }
    void claim()
  }, [isLoaded, isSignedIn, searchParams])

  if (!isLoaded) return <FullPageSkeleton />
  if (!isSignedIn) return <Navigate to='/sign-in' replace />
  if (claiming) return <FullPageSkeleton />

  return <Navigate to='/vendor/assessments' replace />
}

export default VendorWelcomePage
